// authorHighlightPlugin.js
// Decorates text spans that carry an `author` mark with a per-user background
// colour, making it easy to see "who wrote what" at a glance.
//
// How it works:
//   1. On every document update, iterate all text nodes that have an `author` mark.
//   2. Map each unique `userId` to a stable colour from the AUTHOR_COLORS palette.
//   3. Create Decoration.inline for each span, setting a CSS background.
//
// The plugin is stateless — it re-computes decorations on every update.
// It is gated by the `showAuthors` PluginKey meta flag so the highlight layer
// can be toggled on/off without re-creating the plugin.

import {Plugin, PluginKey} from "prosemirror-state";
import {Decoration, DecorationSet} from "prosemirror-view";

export const authorHighlightKey = new PluginKey("authorHighlight");

/**
 * Accessible, visually distinct highlight colours for author attribution.
 * Chosen to work on white backgrounds with enough contrast for readability.
 */
const AUTHOR_COLORS = [
    "rgba(66, 133, 244, 0.18)",   // blue
    "rgba(234, 67, 53, 0.15)",    // red
    "rgba(52, 168, 83, 0.15)",    // green
    "rgba(251, 188, 4, 0.22)",    // yellow
    "rgba(156, 39, 176, 0.15)",   // purple
    "rgba(0, 150, 136, 0.15)",    // teal
    "rgba(255, 87, 34, 0.15)",    // orange
    "rgba(233, 30, 99, 0.14)",    // pink
];

/**
 * Build a stable mapping from userId → colour, cycling through the palette.
 * Uses a closure-local Map so the same plugin instance always maps the same
 * userId to the same colour within a session.
 */
function makeColorMap() {
    const map = new Map();
    let next = 0;
    return function getColor(userId) {
        if (!map.has(userId)) {
            map.set(userId, AUTHOR_COLORS[next % AUTHOR_COLORS.length]);
            next++;
        }
        return map.get(userId);
    };
}

/**
 * Build a DecorationSet from all `author` marks in the document.
 */
function buildDecorations(doc, schema, getColor) {
    const markType = schema.marks.author;
    if (!markType) return DecorationSet.empty;

    const decorations = [];

    doc.descendants((node, pos) => {
        if (!node.isText) return;
        const authorMark = node.marks.find((m) => m.type === markType);
        if (!authorMark) return;

        const {userId} = authorMark.attrs;
        if (!userId) return;

        const color = getColor(userId);
        decorations.push(
            Decoration.inline(pos, pos + node.nodeSize, {
                style: `background-color: ${color};`,
                class: "pm-author-highlight",
                "data-author-id": userId,
                title: `Author: ${userId}`,
            })
        );
    });

    return DecorationSet.create(doc, decorations);
}

/**
 * Create the author-highlight plugin.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=false]  - Start in enabled (visible) state
 * @returns {import('prosemirror-state').Plugin}
 */
export function authorHighlightPlugin({enabled = false} = {}) {
    const getColor = makeColorMap();

    return new Plugin({
        key: authorHighlightKey,

        state: {
            init(_config, state) {
                if (!enabled) return {enabled: false, decorations: DecorationSet.empty};
                return {
                    enabled: true,
                    decorations: buildDecorations(state.doc, state.schema, getColor),
                };
            },
            apply(tr, prev) {
                // Allow external code to toggle via tr.setMeta(authorHighlightKey, { enabled: bool })
                const meta = tr.getMeta(authorHighlightKey);
                const nextEnabled = meta != null ? Boolean(meta.enabled) : prev.enabled;

                if (!nextEnabled) {
                    return {enabled: false, decorations: DecorationSet.empty};
                }

                // Recompute on doc change; otherwise just map positions
                const decorations = tr.docChanged
                    ? buildDecorations(tr.doc, tr.doc.type.schema, getColor)
                    : prev.decorations.map(tr.mapping, tr.doc);

                return {enabled: true, decorations};
            },
        },

        props: {
            decorations(state) {
                return authorHighlightKey.getState(state)?.decorations ?? DecorationSet.empty;
            },
        },
    });
}

/**
 * Toggle author highlighting on or off.
 * @param {import('prosemirror-view').EditorView} view
 * @param {boolean} enabled
 */
export function setAuthorHighlight(view, enabled) {
    const tr = view.state.tr.setMeta(authorHighlightKey, {enabled});
    view.dispatch(tr);
}
